// src/modals/AchievementModal.jsx
// Phase 37 Plan 06 — extracted from src/App.jsx (was lines 7025-7250 pre-Plan-37-06).
//
// Scope: MO-09 — personal achievement card with canvas (Home Panel completion).
// Pre-renders 5 platform share-card images via the sibling share-canvas helper.
// Two App.jsx-private helpers (countCompletedTasks, getMonthColours) move with
// this modal since AchievementModal is their sole consumer.
// UAT2-GAP-06 — exported for testing.

import { useState, useEffect } from 'react';
import { Share2, Download, Copy } from 'lucide-react';
import {
  renderAchievementCardForPlatform,
  renderAchievementCard,
} from './share-canvas.js';
import { logEvent } from '../data/events.js';
import { logAffiliateClick, FEATURE_SOURCES } from '../data/affiliates.js';
import { MONTH_FULL, CURRENT_MONTH, CURRENT_YEAR } from '../constants.js';

// Aggregate colour data for the current month from plant collection
function getMonthColours(plants, month) {
  const colourMap = {};
  // Near-white/near-background colours that look blank on the card
  const blankHexes = new Set(['#ffffff','#fff','#fafafa','#f7f5f0','#f5f5f5','#fefefe','#f0f0f0']);
  plants.forEach(p => {
    const c = p.colour && p.colour[month];
    if (c && c.hex && c.label && !blankHexes.has(c.hex.toLowerCase())) {
      if (!colourMap[c.hex]) colourMap[c.hex] = { hex: c.hex, label: c.label || '', count: 0 };
      colourMap[c.hex].count++;
    }
  });
  return Object.values(colourMap).sort((a, b) => b.count - a.count);
}

// Count completed (done) tasks for a given month/year
function countCompletedTasks(plants, completions, month, year) {
  let count = 0;
  plants.forEach(p => {
    (p.tasks[month] || []).forEach(t => {
      if (t.type !== 'note' && completions[`done:${p.id}:${t.type}:${month}:${year}`]) count++;
    });
  });
  return count;
}

export function AchievementModal({ plants, completions, onClose }) {
  const [previewSrc, setPreviewSrc] = useState(null);
  const [channelFiles, setChannelFiles] = useState({});
  const [copyConfirm, setCopyConfirm] = useState(false);
  const monthName = MONTH_FULL[CURRENT_MONTH - 1];
  const shareUrl = window.location.origin + '/?utm_source=share';

  // Per-channel copy variants — UK English, tuned per platform (CONTEXT.md locked strings).
  // X: ≤240 chars (leaves headroom for URL + hashtags inside X's 280-char limit).
  // LinkedIn: long-form, no hashtags. Instagram/WhatsApp: image-forward, ends with URL.
  const copyFor = {
    x:         `Just completed all my ${monthName} garden tasks with Tend — know exactly what to do in your garden this month 🌱 #gardening #TendApp`,
    facebook:  `I'm keeping my garden on track with Tend — it shows me exactly what needs doing each month, from pruning to feeding. All ${monthName} jobs done!`,
    instagram: `${monthName} garden tasks complete 🌿 Using Tend to stay on top of pruning, feeding, and care — try it at ${shareUrl}`,
    linkedin:  `Completed all my ${monthName} garden-care tasks using Tend — a personalised planner that shows the right job at the right time. Helpful for anyone who wants their garden looked after without guesswork.`,
    whatsapp:  `All ${monthName} garden tasks done with Tend 🌱 It tells you exactly what to do each month. Worth a look: ${shareUrl}`,
  };

  // Pre-render 5 platform Files in parallel on mount.
  // The cancelled flag prevents setChannelFiles being called after unmount.
  useEffect(() => {
    let cancelled = false;
    const tasksDone = countCompletedTasks(plants, completions, CURRENT_MONTH, CURRENT_YEAR);
    const plantCount = plants.length;
    const plantTypes = new Set(plants.map(p => p.type)).size;
    const colours = getMonthColours(plants, CURRENT_MONTH);
    const props = { monthName, tasksDone, plantCount, plantTypes, colours };
    const channelIds = ['x', 'facebook', 'instagram', 'linkedin', 'whatsapp'];

    Promise.all(channelIds.map(ch =>
      renderAchievementCardForPlatform(ch, props).then(canvas => new Promise(resolve => {
        canvas.toBlob(blob => {
          if (!blob) { resolve([ch, null]); return; }
          const filename = `tend-${monthName.toLowerCase()}-${ch}.png`;
          resolve([ch, new File([blob], filename, { type: 'image/png' })]);
        }, 'image/png');
      }))
    )).then(entries => {
      if (cancelled) return;
      setChannelFiles(Object.fromEntries(entries.filter(([, f]) => f)));
    });

    return () => { cancelled = true; };
  }, [plants, completions, monthName]);

  // Preview effect — uses Instagram 1080×1080 canvas (square looks best in-modal).
  useEffect(() => {
    let cancelled = false;
    const tasksDone = countCompletedTasks(plants, completions, CURRENT_MONTH, CURRENT_YEAR);
    const plantCount = plants.length;
    const plantTypes = new Set(plants.map(p => p.type)).size;
    const colours = getMonthColours(plants, CURRENT_MONTH);

    renderAchievementCard({ monthName, tasksDone, plantCount, plantTypes, colours })
      .then(canvas => {
        if (cancelled) return;
        setPreviewSrc(canvas.toDataURL('image/png'));
      });
    return () => { cancelled = true; };
  }, [plants, completions, monthName]);

  const shareText = `I completed all my ${monthName} garden tasks! Check out Tend — a personalised garden planner:`;
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl);

  // logShare writes share-achievement-{channel} — discriminator for Phase 32 analytics.
  const logShare = (channel) => {
    logEvent('share_tapped', { type: 'achievement', channel });
    logAffiliateClick({ linkId: `share-achievement-${channel}`, feature: FEATURE_SOURCES.HOME, month: CURRENT_MONTH });
  };

  // Per-channel click handler: prefer native-share with per-platform File on mobile;
  // silently fall through to window.open intent URL on desktop or navigator.share rejection.
  const handleChannelClick = async (channel, url) => {
    logShare(channel);
    const file = channelFiles[channel];
    if (file && typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'My garden on Tend',
          text: copyFor[channel],
          url: shareUrl,
          files: [file],
        });
        return;
      } catch (_) {
        // User cancel, permission denied, or platform rejection — silent fallthrough.
      }
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    logShare('copy');
    try { await navigator.clipboard.writeText(shareUrl); setCopyConfirm(true); setTimeout(() => setCopyConfirm(false), 2500); } catch (_) {}
  };

  const handleNativeShare = async () => {
    logShare('native');
    const instagramFile = channelFiles.instagram;
    try {
      const shareData = { title: 'My garden on Tend', text: shareText, url: shareUrl };
      if (instagramFile && navigator.canShare && navigator.canShare({ files: [instagramFile] })) {
        await navigator.share({ ...shareData, files: [instagramFile] });
      } else {
        await navigator.share(shareData);
      }
    } catch (_) {}
  };

  const handleSaveCard = () => {
    if (!previewSrc) return;
    logShare('save');
    const a = document.createElement('a');
    a.href = previewSrc;
    a.download = `tend-${monthName.toLowerCase()}-${CURRENT_YEAR}.png`;
    a.click();
  };

  const canNativeShare = typeof navigator !== 'undefined' && navigator.share;
  const instagramFile = channelFiles.instagram;
  const canShareFiles = canNativeShare && instagramFile && navigator.canShare && navigator.canShare({ files: [instagramFile] });

  const channels = [
    { id: 'whatsapp', label: 'WhatsApp', url: `https://wa.me/?text=${encodedText}%20${encodedUrl}` },
    { id: 'facebook', label: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
    { id: 'x', label: 'X', url: `https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}` },
    { id: 'linkedin', label: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}` },
  ];

  const chStyle = {
    display:"flex",alignItems:"center",justifyContent:"center",gap:6,
    flex:"1 1 45%",padding:"10px 0",background:"var(--c-surface)",color:"var(--c-heading)",
    border:"1px solid var(--c-border)",borderRadius:12,fontSize:'var(--font-small)',fontWeight:600,
    cursor:"pointer",textDecoration:"none",minWidth:0,
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)"}}/>
      <div onClick={e => e.stopPropagation()} style={{
        position:"relative",zIndex:1001,background:"var(--c-bg)",borderRadius:18,padding:24,
        maxWidth:400,width:"calc(100% - 32px)",maxHeight:"90vh",overflowY:"auto",
      }}>
        {/* Test probe — hidden element exposing channelFiles count for Vitest assertions */}
        <span data-testid="channel-files-count" className="hidden">{Object.keys(channelFiles).length}</span>

        <h2 style={{fontSize:"var(--font-h2)",fontWeight:600,color:"var(--c-heading)",margin:"0 0 8px",fontFamily:"var(--font-family-display)"}}>Share your achievement</h2>
        <p style={{fontSize:"var(--font-body)",color:"var(--c-text-2)",margin:"0 0 16px",lineHeight:1.5}}>
          All {monthName} garden tasks complete! Share with a friend who loves gardening.
        </p>

        {/* Canvas preview */}
        {previewSrc && (
          <img src={previewSrc} alt="achievement preview"
            style={{width:"100%",borderRadius:12,border:"1px solid var(--c-border)",marginBottom:16}}/>
        )}

        {/* Primary: native share with image (mobile) */}
        {canNativeShare && (
          <button onClick={handleNativeShare} style={{
            display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            width:"100%",padding:"12px 0",background:"var(--c-nav-bg)",color:"#fff",
            border:"none",borderRadius:20,fontSize:'var(--font-body)',fontWeight:600,cursor:"pointer",marginBottom:8,
          }}>
            <Share2 size={16}/> {canShareFiles ? 'Share card' : 'Share'}
          </button>
        )}

        {/* Save card image */}
        {previewSrc && (
          <button onClick={handleSaveCard} style={{
            display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            width:"100%",padding:"10px 0",background:"var(--c-surface)",color:"var(--c-heading)",
            border:"1px solid var(--c-border)",borderRadius:20,fontSize:'var(--font-small)',fontWeight:600,cursor:"pointer",marginBottom:12,
          }}>
            <Download size={14}/> Save card image
          </button>
        )}

        {/* Share via specific channels — each button dispatches through handleChannelClick */}
        <p style={{fontSize:'var(--font-tiny)',color:"var(--c-text-3)",margin:"0 0 8px",fontWeight:600}}>Share a link via</p>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:8}}>
          {channels.map(ch => (
            <button key={ch.id} data-testid={`channel-btn-${ch.id}`}
              onClick={(e) => { e.preventDefault(); handleChannelClick(ch.id, ch.url); }}
              style={chStyle}>
              {ch.label}
            </button>
          ))}
        </div>

        {/* Copy link */}
        <button onClick={handleCopy} style={{
          display:"flex",alignItems:"center",justifyContent:"center",gap:6,
          width:"100%",padding:"10px 0",background:"var(--c-surface)",color:"var(--c-heading)",
          border:"1px solid var(--c-border)",borderRadius:12,fontSize:'var(--font-small)',fontWeight:600,cursor:"pointer",marginBottom:8,
        }}>
          <Copy size={14}/> Copy link
        </button>
        {copyConfirm && (
          <p style={{fontSize:'var(--font-small)',color:"var(--c-success)",textAlign:"center",margin:"0 0 8px"}}>Link copied to clipboard</p>
        )}

        <button onClick={onClose} style={{
          width:"100%",padding:"10px 0",background:"var(--c-border)",color:"var(--c-text-2)",
          border:"none",borderRadius:20,fontSize:'var(--font-body)',fontWeight:600,cursor:"pointer",
        }}>Close</button>
      </div>
    </div>
  );
}
